import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import PublicLayout from "@/components/layout/PublicLayout";
import useSEO, { breadcrumbSchema } from "@/hooks/useSEO";

// Pricing tiers are shared with Home preview so both surfaces stay in sync.
// Priced per unit before bulk discounts; every card links to /contact for a
// tailored quote (order size and destination materially change the number).
export const PRICING_TIERS = [
  {
    slug: "plastic-500ml",
    badge: "Most Popular",
    tag: "Bulk Discount",
    name: "Easy Recycle Plastic Bottle",
    price: "$1.08",
    unit: "Per Bottle",
    size: "500 mL / 16.9 oz",
    highlights: ["Fully-custom label & shrink-sleeve", "Case of 24 bottles", "MOQ: 1 pallet (65 cases)"],
  },
  {
    slug: "aluminum-473ml",
    tag: "Bulk Discount",
    name: "Premium Aluminum Collection",
    price: "$1.68",
    unit: "Per Can",
    size: "473 mL / 16 oz",
    highlights: ["Elevated shelf presentation", "Case of 24 cans", "MOQ: 1 pallet (70 cases)"],
  },
  {
    slug: "plastic-1l",
    tag: "Bulk Discount",
    name: "Premium Liter Bottle",
    price: "$1.28",
    unit: "Per Bottle",
    size: "1 L / 33.8 oz",
    highlights: ["Premium liter format", "Case of 12 bottles", "MOQ: 1 pallet"],
  },
];

export const PricingCard = ({ tier }) => (
  <div className={`ft-card fx-hover-lift relative flex flex-col ${tier.badge ? "border-[var(--ft-gold)]" : ""}`}
       data-testid={`pricing-card-${tier.slug}`}>
    {tier.badge && (
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[var(--ft-gold)] text-[var(--ft-black)] rounded-full text-[0.65rem] font-bold uppercase tracking-[0.2em]">
        {tier.badge}
      </div>
    )}
    <div className="text-[0.65rem] uppercase tracking-[0.22em] font-semibold text-[var(--ft-gold)] mb-2">{tier.tag}</div>
    <h3 className="text-xl font-bold text-[var(--ft-black)] tracking-tight mb-4">{tier.name}</h3>
    <div className="ft-gold-rule mb-4" />
    <div className="flex items-baseline gap-2 mb-1">
      <span className="text-4xl font-bold tracking-tight text-[var(--ft-black)]">{tier.price}</span>
      <span className="text-sm text-[var(--ft-muted)]">{tier.unit}</span>
    </div>
    <div className="text-sm text-[var(--ft-charcoal)] font-medium">{tier.size}</div>
    <ul className="space-y-2 mt-6 mb-8 text-sm text-[var(--ft-charcoal)]">
      {tier.highlights.map((h) => (
        <li key={h} className="flex items-start gap-2">
          <Check size={14} className="text-[var(--ft-gold)] mt-0.5 shrink-0" strokeWidth={2.5} />
          <span>{h}</span>
        </li>
      ))}
    </ul>
    <Link to="/contact" className="ft-btn-gold mt-auto justify-center" data-testid={`pricing-quote-${tier.slug}`}>
      Get a Quote <ArrowRight size={16} />
    </Link>
  </div>
);

const Pricing = () => {
  useSEO({
    title: "Private Label Beverage Pricing — FLOWTALIS™",
    description: "Transparent starting prices for professionally branded beverages and custom packaging solutions created for businesses, organizations, events, and growing brands.",
    canonical: "https://flowtalis.com/pricing",
    jsonLd: breadcrumbSchema([
      { label: "Home", href: "/" },
      { label: "Pricing", href: "/pricing" },
    ]),
  });
  return (
    <PublicLayout>
      <section className="ft-section-dark py-24">
        <div className="ft-container max-w-3xl ft-fade-in relative">
          <div className="text-[0.6875rem] uppercase tracking-[0.32em] text-[var(--ft-gold)] font-semibold mb-6">Pricing</div>
          <span className="ft-gold-rule-lg" />
          <h1 className="mt-6 text-white text-[clamp(2.25rem,4.5vw,3.5rem)] font-bold leading-[1.02] tracking-tight mb-6">
            Private Label Beverage <span className="ft-serif text-[var(--ft-gold)]">Pricing</span>.
          </h1>
          <p className="text-white/60 text-lg leading-relaxed max-w-2xl">
            Transparent starting prices for professionally branded beverages and custom packaging solutions created
            for businesses, organizations, events, and growing brands.
          </p>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="ft-container grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="pricing-grid">
          {PRICING_TIERS.map((tier) => <PricingCard key={tier.slug} tier={tier} />)}
        </div>
        <div className="ft-container max-w-3xl mt-10" data-testid="pricing-disclaimer">
          <p className="text-xs text-[var(--ft-muted)] leading-relaxed text-center italic">
            Pricing shown reflects standard starting rates. Final pricing may vary based on beverage type, packaging,
            order quantity, customization requirements, shipping destination, and manufacturer availability. Volume
            discounts and contract pricing may be available for qualifying recurring orders.
          </p>
        </div>
      </section>

      <section className="ft-section-dark py-24 text-center">
        <div className="ft-container max-w-2xl relative">
          <h2 className="text-white text-3xl md:text-4xl font-bold mb-4 tracking-tight">
            Ready to launch your <span className="ft-serif text-[var(--ft-gold)]">private-label beverage</span>?
          </h2>
          <p className="text-white/60 mb-8 text-lg leading-relaxed">
            Whether you&apos;re creating branded bottled water for your business, event, organization, or retail brand,
            we&apos;re here to help bring your vision to life.
          </p>
          <Link to="/contact" className="ft-btn-gold" data-testid="pricing-cta-bottom">
            Get a Quote <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
};

export default Pricing;
